import { useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import {
  aiService,
  ReviewPreparationInsights,
  KPAData as AIKPAData,
  KPADiscussionGuidance,
  RatingRationale,
  ReviewSynthesis,
  StakeholderFeedbackSummary
} from '@/services/aiService'
import { reviewService } from '@/services/api/reviewService'
import type { DiscussionPoint } from '@/services/api/reviewService'
import { HolisticFeedbackAnalysis } from '@/services/api/feedbackService'
import { EnhancedEvidenceEntry, KPAData } from '@/types/evidence'

interface UseReviewAIParams {
  memberId: string
  memberName: string
  reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc'
  reviewPeriod: 'MIDYEAR' | 'YEAREND'
  isFormalReview: boolean
  evidence: EnhancedEvidenceEntry[]
  holisticFeedback: HolisticFeedbackAnalysis | null
  reviewSessionId: string | null
  // When true, the AI prep is generated in the team member's own voice
  // (self-preparation, second person) rather than the people-leader voice.
  isTeamMember?: boolean
}

export function useReviewAI({
  memberId,
  memberName,
  reviewType,
  reviewPeriod,
  isFormalReview,
  evidence,
  holisticFeedback,
  reviewSessionId,
  isTeamMember = false,
}: UseReviewAIParams) {
  const [aiInsights, setAIInsights] = useState<ReviewPreparationInsights | null>(null)
  const [isLoadingAIInsights, setIsLoadingAIInsights] = useState(false)

  const [kpaGuidance, setKpaGuidance] = useState<Record<string, KPADiscussionGuidance>>({})
  const [loadingGuidanceFor, setLoadingGuidanceFor] = useState<string | null>(null)
  const [showGuidanceFor, setShowGuidanceFor] = useState<string | null>(null)

  const [ratingRationale, setRatingRationale] = useState<Record<string, RatingRationale>>({})
  const [loadingRationaleFor, setLoadingRationaleFor] = useState<string | null>(null)

  const [reviewSynthesis, setReviewSynthesis] = useState<ReviewSynthesis | null>(null)
  const [loadingSynthesis, setLoadingSynthesis] = useState(false)

  const getStakeholderFeedbackSummary = useCallback((): StakeholderFeedbackSummary | undefined => {
    if (!holisticFeedback || !holisticFeedback.analysis_available || !holisticFeedback.analysis) {
      return undefined
    }

    const analysis = holisticFeedback.analysis
    return {
      available: true,
      totalFeedbackCycles: holisticFeedback.total_feedback_cycles,
      totalResponses: holisticFeedback.total_responses,
      overallSummary: analysis.overall_summary,
      keyStrengths: analysis.key_strengths,
      developmentAreas: analysis.development_areas,
      themes: analysis.themes?.map(t => ({
        theme: t.theme,
        sentiment: t.sentiment,
        frequency: t.frequency
      })),
      trendDirection: analysis.trend_analysis?.direction,
      recommendations: analysis.actionable_recommendations
    }
  }, [holisticFeedback])

  const loadAIInsights = useCallback(async (activeKpaData: KPAData[], currentKPA: KPAData | undefined) => {
    if (!currentKPA || activeKpaData.length === 0) {
      toast.error('No KPA data available for analysis')
      return
    }

    setIsLoadingAIInsights(true)

    try {
      const transformedKPAs: AIKPAData[] = activeKpaData.map(kpa => {
        const activeObjectives = kpa.individualObjectives?.filter((obj: any) =>
          obj.status !== 'rejected' &&
          obj.status !== 'pending-approval' &&
          obj.status !== 'pending_approval'
        ) || []

        const activeKPIIds = new Set<string>()
        activeObjectives.forEach((obj: any) => {
          obj.kpis?.forEach((kpi: any) => {
            activeKPIIds.add(kpi.id)
          })
        })

        const activeUnderlyingKPIs = (kpa.underlyingKPIs || []).filter((kpi: any) =>
          activeKPIIds.has(kpi.id)
        )

        const kpaEvidence = evidence.filter(e => {
          // kpa.id is the name slug in the conduct-review shape; the API's
          // kpaId is the stringified numeric PK — match both (see numericId
          // on the manager kpaData groups).
          const matchesKPA = e.kpaId === kpa.id ||
            ((kpa as any).numericId != null && e.kpaId != null &&
              String(e.kpaId) === String((kpa as any).numericId)) ||
            activeObjectives.some((obj: any) =>
              obj.id.toString() === e.objectiveId ||
              obj.kpis?.some((kpi: any) => kpi.id === e.kpiId)
            )
          if (!matchesKPA) return false
          if (!e.reviewPeriod) return true
          // Dual-period entries surface in either review's prep
          if ((e as any).appliesToBothPeriods) return true
          return e.reviewPeriod === reviewPeriod
        })

        return {
          id: kpa.id,
          name: kpa.name,
          description: kpa.description || '',
          objectives: activeObjectives.map((obj: any) => ({
            id: obj.id,
            title: obj.title,
            description: obj.description || '',
            status: obj.status || 'in_progress',
            progress: obj.progress || 0,
            kpis: (obj.kpis || []).map((kpi: any) => ({
              id: kpi.id,
              name: kpi.name,
              description: kpi.description,
              unit: kpi.unit,
              current_value: kpi.current_value,
              target_value: kpi.target_value,
              progress: kpi.progress,
              measurement_mode: kpi.measurement_mode,
              measurement_direction: kpi.measurement_direction
            }))
          })),
          kpis: activeUnderlyingKPIs.map((kpi: any) => ({
            id: kpi.id,
            name: kpi.name,
            description: kpi.description,
            unit: kpi.unit,
            current_value: kpi.current_value,
            target_value: kpi.target_value,
            progress: kpi.progress,
            measurement_mode: kpi.measurement_mode,
            measurement_direction: kpi.measurement_direction
          })),
          evidence: kpaEvidence.map(e => ({
            id: e.id,
            title: e.title,
            type: e.type,
            classification: e.classification,
            measurementValue: (e as any).measurementValue,
            kpiId: e.kpiId,
            objectiveId: e.objectiveId
          }))
        }
      })

      const stakeholderFeedback = getStakeholderFeedbackSummary()

      const insights = await aiService.generateReviewPreparationInsights({
        employeeId: memberId,
        employeeName: memberName,
        reviewType: reviewType as any,
        kpas: transformedKPAs,
        stakeholderFeedback,
        perspective: isTeamMember ? 'self' : 'manager',
      })

      setAIInsights(insights)
      toast.success(isTeamMember ? 'Readiness check ready' : 'AI insights generated successfully!')

    } catch (error) {
      console.error('Failed to generate AI insights:', error)
      toast.error('Failed to generate AI insights. Please try again.')
    } finally {
      setIsLoadingAIInsights(false)
    }
  }, [evidence, memberId, memberName, reviewType, reviewPeriod, getStakeholderFeedbackSummary, isTeamMember])

  const loadKPAGuidance = useCallback(async (
    currentKPA: KPAData | undefined,
    currentKPAEvidence: EnhancedEvidenceEntry[],
    kpaDiscussionPoints: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }>
  ) => {
    if (!currentKPA) {
      toast.error('No KPA selected')
      return
    }

    if (kpaGuidance[currentKPA.id]) {
      setShowGuidanceFor(showGuidanceFor === currentKPA.id ? null : currentKPA.id)
      return
    }

    setLoadingGuidanceFor(currentKPA.id)
    setShowGuidanceFor(currentKPA.id)

    try {
      const existingPoints = kpaDiscussionPoints[currentKPA.id]

      const guidance = await aiService.generateKPADiscussionGuidance({
        kpaId: currentKPA.id,
        kpaName: currentKPA.name,
        kpaDescription: currentKPA.description || '',
        objectives: currentKPA.individualObjectives?.filter((obj: any) => obj.status !== 'rejected' && obj.status !== 'pending-approval' && obj.status !== 'pending_approval').map((obj: any) => ({
          id: obj.id,
          title: obj.title,
          description: obj.description || '',
          status: obj.status || 'in_progress',
          progress: obj.progress || 0,
          kpis: (obj.kpis || []).map((kpi: any) => ({
            id: kpi.id,
            name: kpi.name,
            unit: kpi.unit,
            current_value: kpi.current_value,
            target_value: kpi.target_value,
            measurement_mode: kpi.measurement_mode,
            measurement_direction: kpi.measurement_direction
          }))
        })) || [],
        kpis: (currentKPA.underlyingKPIs || []).map((kpi: any) => ({
          id: kpi.id,
          name: kpi.name,
          unit: kpi.unit,
          current_value: kpi.current_value,
          target_value: kpi.target_value,
          measurement_mode: kpi.measurement_mode,
          measurement_direction: kpi.measurement_direction
        })),
        evidence: currentKPAEvidence.map(e => ({
          id: e.id,
          title: e.title,
          type: e.type,
          classification: e.classification
        })),
        employeeName: memberName,
        reviewType: reviewType as any,
        existingDiscussionPoints: existingPoints ? {
          wentWell: existingPoints.wentWell || [],
          challenges: existingPoints.challenges || [],
          actions: existingPoints.actions || []
        } : undefined,
        stakeholderFeedback: getStakeholderFeedbackSummary()
      })

      setKpaGuidance(prev => ({
        ...prev,
        [currentKPA.id]: guidance
      }))

      toast.success('Discussion guidance generated!')

    } catch (error) {
      console.error('Failed to generate KPA guidance:', error)
      toast.error('Failed to generate discussion guidance. Please try again.')
      setShowGuidanceFor(null)
    } finally {
      setLoadingGuidanceFor(null)
    }
  }, [memberName, reviewType, kpaGuidance, showGuidanceFor, getStakeholderFeedbackSummary])

  const handleToggleGuidance = useCallback((
    currentKPA: KPAData | undefined,
    currentKPAEvidence: EnhancedEvidenceEntry[],
    kpaDiscussionPoints: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }>
  ) => {
    if (!currentKPA) return

    if (kpaGuidance[currentKPA.id]) {
      setShowGuidanceFor(showGuidanceFor === currentKPA.id ? null : currentKPA.id)
    } else {
      loadKPAGuidance(currentKPA, currentKPAEvidence, kpaDiscussionPoints)
    }
  }, [kpaGuidance, showGuidanceFor, loadKPAGuidance])

  const loadRatingRationale = useCallback(async (
    kpaId: string,
    activeKpaData: KPAData[],
    kpaDiscussionPoints: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }>,
    kpaRatings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>
  ) => {
    const kpa = activeKpaData.find(k => k.id === kpaId)
    if (!kpa) return

    setLoadingRationaleFor(kpaId)

    try {
      const kpaEvidence = evidence.filter(e => {
        const matchesKPA = e.kpaId === kpaId ||
          ((kpa as any).numericId != null && e.kpaId != null &&
            String(e.kpaId) === String((kpa as any).numericId)) ||
          kpa.underlyingKPIs?.some((kpi: any) => kpi.id === e.kpiId)
        if (!matchesKPA) return false
        if (!e.reviewPeriod) return true
        // Dual-period entries are valid input for either rationale
        if ((e as any).appliesToBothPeriods) return true
        return e.reviewPeriod === reviewPeriod
      })

      const existingPoints = kpaDiscussionPoints[kpaId]

      const rationale = await aiService.generateRatingRationale({
        kpaId,
        kpaName: kpa.name,
        kpaDescription: kpa.description || '',
        objectives: kpa.individualObjectives?.filter((obj: any) => obj.status !== 'rejected' && obj.status !== 'pending-approval' && obj.status !== 'pending_approval').map((obj: any) => ({
          id: obj.id,
          title: obj.title,
          description: obj.description || '',
          status: obj.status || 'in_progress',
          progress: obj.progress || 0,
          kpis: (obj.kpis || []).map((kpi: any) => ({
            id: kpi.id,
            name: kpi.name,
            unit: kpi.unit,
            current_value: kpi.current_value,
            target_value: kpi.target_value,
            measurement_mode: kpi.measurement_mode,
            measurement_direction: kpi.measurement_direction
          }))
        })) || [],
        kpis: (kpa.underlyingKPIs || []).map((kpi: any) => ({
          id: kpi.id,
          name: kpi.name,
          unit: kpi.unit,
          current_value: kpi.current_value,
          target_value: kpi.target_value,
          measurement_mode: kpi.measurement_mode,
          measurement_direction: kpi.measurement_direction
        })),
        evidence: kpaEvidence.map(e => ({
          id: e.id,
          title: e.title,
          type: e.type,
          classification: e.classification
        })),
        discussionPoints: existingPoints ? {
          wentWell: existingPoints.wentWell || [],
          challenges: existingPoints.challenges || [],
          actions: existingPoints.actions || []
        } : undefined,
        employeeName: memberName,
        reviewType: reviewType as any,
        proposedRating: kpaRatings[kpaId] || undefined,
        stakeholderFeedback: getStakeholderFeedbackSummary()
      })

      setRatingRationale(prev => ({
        ...prev,
        [kpaId]: rationale
      }))

      toast.success('Impact & Growth Indicator analysis complete!')

    } catch (error) {
      console.error('Rating rationale generation failed:', error)
      toast.error('Failed to generate indicator rationale')
    } finally {
      setLoadingRationaleFor(null)
    }
  }, [evidence, memberName, reviewType, reviewPeriod, getStakeholderFeedbackSummary])

  const handleAcceptRatingSuggestion = useCallback((kpaId: string, rating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr', setKpaRatings: React.Dispatch<React.SetStateAction<Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>>>) => {
    setKpaRatings(prev => ({
      ...prev,
      [kpaId]: rating
    }))
    toast.success('Impact & Growth Indicator updated to AI suggestion')
  }, [])

  const loadReviewSynthesis = useCallback(async (
    activeKpaData: KPAData[],
    kpaRatings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>,
    overallRating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null,
    kpaDiscussionPoints: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }>,
    // Free-text rating justifications the People Leader wrote per-KPA and overall.
    // Critical: when a check-in happened offline (no impact-portfolio items, so KPI
    // measurements are empty), these are the ONLY real evidence of progress — without
    // them the AI sees 0% everywhere and wrongly reports "lack of progress".
    managerComments: Record<string, string> = {},
    overallJustification: string = ''
  ) => {
    setLoadingSynthesis(true)

    try {
      const enrichedKpaRatings = activeKpaData.map(kpa => ({
        kpaId: kpa.id,
        kpaName: kpa.display_name || kpa.name,
        rating: isFormalReview ? (kpaRatings[kpa.id] || 'not-yet-full') : undefined,
        // The People Leader's written justification for this KPA's rating.
        justification: managerComments[kpa.id] || '',
        objectives: (kpa.individualObjectives || []).filter((obj: any) => obj.status !== 'rejected' && obj.status !== 'pending-approval' && obj.status !== 'pending_approval').map((obj: any) => ({
          id: obj.id,
          title: obj.title,
          description: obj.description || '',
          status: obj.status || 'in_progress',
          progress: obj.progress || 0,
          functionalArea: obj.functional_area || '',
          startDate: obj.start_date,
          endDate: obj.end_date
        })),
        kpis: (kpa.underlyingKPIs || []).map((kpi: any) => {
          const current = kpi.current_value || 0
          const target = kpi.target_value || 0
          const mode = kpi.measurement_mode || 'numeric'
          const direction = kpi.measurement_direction || 'higher_is_better'
          const unit = (kpi.unit || '').toLowerCase()

          let achievement = 0
          if (mode === 'rating' || ['status', 'rag', 'rating'].includes(unit)) {
            achievement = current
          } else if (mode === 'binary' || ['achieved', 'yes/no', 'complete'].includes(unit)) {
            achievement = current >= 100 ? 100 : 0
          } else if (direction === 'lower_is_better') {
            achievement = current > 0 && target > 0
              ? Math.min(100, Math.round((target / current) * 100))
              : 0
          } else {
            achievement = target > 0
              ? Math.round((current / target) * 100)
              : 0
          }

          return {
            id: kpi.id,
            name: kpi.name,
            description: kpi.description || '',
            currentValue: current,
            targetValue: target,
            unit: kpi.unit || '',
            achievement,
            status: kpi.status || 'on-track',
            trend: kpi.trend || 'stable',
            frequency: kpi.frequency || 'quarterly',
            measurementHistory: kpi.measurement_history || [],
            lastMeasured: kpi.last_measured_at,
            measurementMode: mode,
            measurementDirection: direction
          }
        }),
        strategicContext: {
          pillar: (kpa as any).pillar || null,
          functionalArea: (kpa as any).functional_area || kpa.name,
          weightPercentage: (kpa as any).weight_percentage || 0
        }
      }))

      const synthesis = await aiService.generateReviewSynthesis({
        employeeId: memberId,
        employeeName: memberName,
        reviewType: reviewType as any,
        reviewDate: new Date().toISOString(),
        kpaRatings: enrichedKpaRatings,
        overallRating: isFormalReview ? (overallRating || 'not-yet-full') : undefined,
        overallJustification,
        allDiscussionPoints: kpaDiscussionPoints,
        allEvidence: evidence,
        stakeholderFeedback: getStakeholderFeedbackSummary(),
        isFormal: isFormalReview
      })

      setReviewSynthesis(synthesis)
      toast.success('Check-in synthesis generated!')

      if (reviewSessionId) {
        try {
          await reviewService.saveSynthesis(reviewSessionId, synthesis)
        } catch (err) {
          console.error('Failed to persist synthesis:', err)
        }
      }

    } catch (error) {
      console.error('Synthesis generation failed:', error)
      toast.error('Failed to generate check-in synthesis')
    } finally {
      setLoadingSynthesis(false)
    }
  }, [memberId, memberName, reviewType, evidence, getStakeholderFeedbackSummary, isFormalReview, reviewSessionId])

  const handleExportPDF = useCallback(async () => {
    if (!reviewSessionId) return
    try {
      const blob = await reviewService.exportSynthesisPDF(reviewSessionId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${memberName.replace(/\s+/g, '_')}_Synthesis_${new Date().toISOString().slice(0, 10)}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('PDF exported!')
    } catch (error) {
      console.error('PDF export failed:', error)
      toast.error('Failed to export PDF')
    }
  }, [reviewSessionId, memberName])

  const handleSendToEmployee = useCallback(async () => {
    if (!reviewSessionId) return
    try {
      const result = await reviewService.sendSynthesisEmail(reviewSessionId)
      toast.success(`Synthesis sent to ${result.to}`)
    } catch (error) {
      console.error('Send email failed:', error)
      toast.error('Failed to send email. Please try again.')
    }
  }, [reviewSessionId])

  // Remove the generated report (clears it locally + on the server).
  const handleDeleteSynthesis = useCallback(async () => {
    try {
      if (reviewSessionId) {
        await reviewService.deleteSynthesis(reviewSessionId)
      }
      setReviewSynthesis(null)
      toast.success('Synthesis report removed')
    } catch (error) {
      console.error('Failed to remove synthesis:', error)
      toast.error('Failed to remove the report')
    }
  }, [reviewSessionId])

  // Persist a manually-edited report. Stamps provenance so the UI shows "Edited"
  // rather than "AI-Generated".
  const saveEditedSynthesis = useCallback(async (edited: any) => {
    const stamped = { ...edited, manuallyEdited: true, editedAt: new Date().toISOString() }
    setReviewSynthesis(stamped)
    try {
      if (reviewSessionId) {
        await reviewService.saveSynthesis(reviewSessionId, stamped)
      }
      toast.success('Report updated')
    } catch (error) {
      console.error('Failed to save edited synthesis:', error)
      toast.error('Failed to save your edits')
    }
  }, [reviewSessionId])

  return {
    aiInsights,
    isLoadingAIInsights,
    loadAIInsights,
    kpaGuidance,
    loadingGuidanceFor,
    showGuidanceFor,
    loadKPAGuidance,
    handleToggleGuidance,
    ratingRationale,
    loadingRationaleFor,
    loadRatingRationale,
    handleAcceptRatingSuggestion,
    reviewSynthesis,
    setReviewSynthesis,
    loadingSynthesis,
    loadReviewSynthesis,
    handleExportPDF,
    handleSendToEmployee,
    handleDeleteSynthesis,
    saveEditedSynthesis,
  }
}
